import { useCallback, useState, useMemo } from 'react'
import { withDemo } from '@/config/demoMode'
import type { Loan } from '@/types'
import {
  DealTableRow,
  DealTableSortHeader,
  GenericSortHeader,
  dealTableHeadRowClass,
  isLoanTableId,
  useDealTableInteraction,
  useGenericTableSort,
  useLoanTableSort,
} from '@/components/shared/DealTable'
import { riskTierRank } from '@/utils/dealTableSort'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts'
import {
  LayoutDashboard,
  TrendingDown,
  Percent,
  Globe,
  Shield,
  Layers,
} from 'lucide-react'
import { usePortfolioAnalyticsBook } from '@/hooks/useDemoDataAccess'
import type { AnalyticsBook } from '@/pages/liveModuleData'
import CapRateStressTest from '@/components/analytics/CapRateStressTest'
import PortfolioLtvAnalysis from '@/components/analytics/PortfolioLtvAnalysis'
import MarketExposureAnalysis from '@/components/analytics/MarketExposureAnalysis'
import RiskMonitoringDashboard from '@/components/analytics/RiskMonitoringDashboard'
import ValueAtRiskModel from '@/components/analytics/ValueAtRiskModel'
import {
  CHART_LEGEND_STYLE,
  CHART_PIPELINE_COLOR,
  CHART_PORTFOLIO_COLOR,
  CHART_SERIES_COLORS,
  CHART_TOOLTIP_PROPS,
  chartAxisProps,
  chartBarGradient,
  chartFunnelBarGradient,
  chartGridProps,
  chartPieColor,
  chartRiskTierColor,
  chartSeriesColor,
} from '@/config/chartTheme'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmt = {
  money: (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(2)}B` : `$${v.toFixed(1)}M`,
  pct: (v: number) => `${(v * 100).toFixed(1)}%`,
  x: (v: number) => `${v.toFixed(2)}x`,
}

// ---------------------------------------------------------------------------
// Aggregate data from deals
// ---------------------------------------------------------------------------
/** Balance-weighted average over the loans that HAVE the metric; null if none do. */
function weightedAverage(deals: Loan[], pick: (d: Loan) => number | null | undefined): number | null {
  let weight = 0
  let total = 0
  for (const d of deals) {
    const v = pick(d)
    if (typeof v !== 'number' || !Number.isFinite(v)) continue
    weight += d.loanAmount
    total += v * d.loanAmount
  }
  return weight > 0 ? total / weight : null
}

/** The state from "City, ST" or "Street, City, ST 12345". */
function stateOf(location: string): string {
  const last = (location || '').split(',').pop()?.trim() ?? ''
  const withoutZip = last.replace(/\s*\d{5}(-\d{4})?$/, '')
  // "New York NY" (no comma before the state) → "NY".
  const code = withoutZip.match(/(?:^|\s)([A-Z]{2})$/)
  return code ? code[1] : withoutZip || 'Other'
}

function quarterOf(isoDate: string): { key: string; label: string } | null {
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return null
  const q = Math.floor(date.getUTCMonth() / 3) + 1
  const yy = String(date.getUTCFullYear()).slice(2)
  return { key: `${date.getUTCFullYear()}-${q}`, label: `Q${q}'${yy}` }
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Overview figures.
 *
 * Live (`live: true`) computes only from what the funded loans record. The
 * demo-only panels — a hardcoded maturity ladder, a linear-decay conversion
 * funnel, stage velocities, a 12.8% portfolio IRR and a fee "revenue" formula
 * — have no live source, so they come back empty (or null) and the page says
 * which source is missing instead of drawing them.
 */
function usePortfolioData(deals: Loan[], live: boolean) {
  return useMemo(() => {
    const totalAum = deals.reduce((s, d) => s + d.loanAmount, 0)
    const dealCount = deals.length
    if (dealCount === 0 || totalAum === 0) {
      return {
        metrics: {
          totalAum: 0,
          dealCount,
          wtdLtv: null,
          wtdDscr: null,
          wtdDy: null,
          delinquencies: live ? null : 0,
          avgSeasoning: null,
          portfolioIrr: null,
        } as PortfolioMetrics,
        geographic: [],
        propertyTypes: [],
        revenueByProduct: [],
        originators: [],
        maturitySchedule: [],
        funnelStages: [],
      }
    }
    const wtdLtv = weightedAverage(deals, (d) => (d.ltv == null ? null : d.ltv <= 1 ? d.ltv : d.ltv / 100))
    const wtdDscr = weightedAverage(deals, (d) => d.dscr)
    const wtdDy = weightedAverage(deals, (d) => d.debtYield || null)
    // Delinquency is a servicing fact; the live book has no servicing feed,
    // and an overdue covenant trigger is not a missed payment.
    const delinquencies = live
      ? null
      : deals.filter(d => (d.triggers ?? []).some(t => t.status === 'overdue')).length
    const seasoningDays = live
      ? deals
          .map((d) => (d.fundedAt ? (Date.now() - new Date(d.fundedAt).getTime()) / DAY_MS : NaN))
          .filter((n) => Number.isFinite(n) && n >= 0)
      : deals.map((d) => d.daysOpen)
    const avgSeasoning = seasoningDays.length
      ? Math.round(seasoningDays.reduce((s, n) => s + n, 0) / seasoningDays.length)
      : null

    // Geographic concentration
    const geoMap = new Map<string, number>()
    deals.forEach(d => {
      const state = stateOf(d.location)
      geoMap.set(state, (geoMap.get(state) ?? 0) + d.loanAmount)
    })
    const geographic = [...geoMap.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([state, amount]) => ({ name: state, value: amount, pct: amount / totalAum }))

    // Property type concentration
    const typeMap = new Map<string, number>()
    deals.forEach(d => {
      typeMap.set(d.type, (typeMap.get(d.type) ?? 0) + d.loanAmount)
    })
    const propertyTypes = [...typeMap.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([type, amount]) => ({ name: type, value: amount, pct: amount / totalAum }))

    // Revenue by product — an ESTIMATE from an invented formula, demo only.
    const productMap = new Map<string, number>()
    if (!live) {
      deals.forEach(d => {
        const prod = d.product ?? 'Senior Mortgage'
        const revenue = d.loanAmount * 0.01 * (d.debtYield ?? 0.085) * 100 // Estimated fee revenue
        productMap.set(prod, (productMap.get(prod) ?? 0) + revenue)
      })
    }
    const revenueByProduct = [...productMap.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([product, revenue]) => ({ product, revenue: +revenue.toFixed(1) }))

    // Originator performance. Live reads the originator of record, not the
    // current assignee (who may be nobody).
    const originatorMap = new Map<string, { volume: number; deals: number }>()
    deals.forEach(d => {
      const name = live ? d.originator?.trim() || 'Originator not recorded' : d.assignee
      const existing = originatorMap.get(name) ?? { volume: 0, deals: 0 }
      originatorMap.set(name, { volume: existing.volume + d.loanAmount, deals: existing.deals + 1 })
    })
    const originators = [...originatorMap.entries()]
      .sort((a, b) => b[1].volume - a[1].volume)
      .slice(0, 6)
      .map(([name, data]) => ({ name, volume: data.volume, deals: data.deals }))

    // Maturity schedule: live from each funded loan's maturity date.
    let maturitySchedule: Array<{ quarter: string; amount: number }>
    if (live) {
      const byQuarter = new Map<string, { label: string; amount: number }>()
      deals.forEach(d => {
        const q = d.maturity ? quarterOf(d.maturity) : null
        if (!q) return
        const cur = byQuarter.get(q.key) ?? { label: q.label, amount: 0 }
        byQuarter.set(q.key, { label: q.label, amount: cur.amount + d.loanAmount })
      })
      maturitySchedule = [...byQuarter.entries()]
        .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
        .map(([, v]) => ({ quarter: v.label, amount: +v.amount.toFixed(1) }))
    } else {
      // Illustrative quarterly ladder (demo dataset only)
      maturitySchedule = [
        { quarter: "Q2'26", amount: 85.5 },
        { quarter: "Q3'26", amount: 62.0 },
        { quarter: "Q4'26", amount: 145.0 },
        { quarter: "Q1'27", amount: 38.5 },
        { quarter: "Q2'27", amount: 47.0 },
        { quarter: "Q3'27", amount: 78.0 },
        { quarter: "Q4'27", amount: 112.5 },
        { quarter: "Q1'28", amount: 52.0 },
        { quarter: "Q2'28", amount: 95.0 },
        { quarter: "Q3'28", amount: 68.0 },
        { quarter: "Q4'28", amount: 130.0 },
      ]
    }

    // Conversion funnel (demo dataset only: the live book keeps no stage history)
    const funnelStages = live
      ? []
      : [
          { stage: 'Triage', count: 100, rate: 100 },
          { stage: 'Pre-screen', count: 84, rate: 84 },
          { stage: 'Post-screen Quote', count: 72, rate: 72 },
          { stage: 'TS Issued', count: 58, rate: 58 },
          { stage: 'TS Signed', count: 45, rate: 45 },
          { stage: 'IC Approved', count: 38, rate: 38 },
          { stage: 'Rate Lock', count: 32, rate: 32 },
          { stage: 'Funded', count: 28, rate: 28 },
        ]

    return {
      metrics: {
        totalAum,
        dealCount,
        wtdLtv,
        wtdDscr,
        wtdDy,
        delinquencies,
        avgSeasoning,
        portfolioIrr: live ? null : 0.128,
      } as PortfolioMetrics,
      geographic,
      propertyTypes,
      revenueByProduct,
      originators,
      maturitySchedule,
      funnelStages,
    }
  }, [deals, live])
}

interface PortfolioMetrics {
  totalAum: number
  dealCount: number
  wtdLtv: number | null
  wtdDscr: number | null
  wtdDy: number | null
  delinquencies: number | null
  avgSeasoning: number | null
  portfolioIrr: number | null
}

/** Why a panel is empty on the live book: the source that is missing. */
function NoSource({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-dashed border-amber-400/10 px-4 py-6 text-center text-[11px] leading-relaxed text-stone-500">
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function MetricStrip({ metrics }: { metrics: PortfolioMetrics }) {
  const orDash = <T,>(v: T | null, f: (x: T) => string) => (v == null ? '—' : f(v))
  const items = [
    { label: 'AUM', value: fmt.money(metrics.totalAum) },
    { label: 'Deals', value: String(metrics.dealCount) },
    { label: 'Wtd LTV', value: orDash(metrics.wtdLtv, fmt.pct) },
    { label: 'Wtd DSCR', value: orDash(metrics.wtdDscr, fmt.x) },
    { label: 'Debt Yield', value: orDash(metrics.wtdDy, fmt.pct) },
    { label: 'Delinquencies', value: orDash(metrics.delinquencies, String) },
    { label: 'Avg Seasoning', value: orDash(metrics.avgSeasoning, (d) => `${d}d`) },
    { label: 'Portfolio IRR', value: orDash(metrics.portfolioIrr, fmt.pct) },
  ]

  return (
    <div className="grid grid-cols-4 gap-3 lg:grid-cols-8">
      {items.map((item) => (
        <div key={item.label} className="card px-3 py-2.5">
          <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-stone-600">{item.label}</div>
          <div className="mt-1.5 text-lg font-semibold text-stone-100">{item.value}</div>
        </div>
      ))}
    </div>
  )
}

function ConcentrationBar({ data, label }: { data: Array<{ name: string; value: number; pct: number }>; label: string }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-600 mb-3">{label}</div>
      <div className="space-y-2.5">
        {data.map((item, i) => (
          <div key={item.name} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-stone-300">{item.name}</span>
              <span className="font-mono text-stone-400">{(item.pct * 100).toFixed(0)}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/[0.04]">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${item.pct * 100}%`,
                  background: chartBarGradient(i, 1),
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function FunnelChart({ stages }: { stages: Array<{ stage: string; count: number; rate: number }> }) {
  return (
    <div className="space-y-2">
      {stages.map((s, i) => (
        <div key={s.stage} className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-stone-300">{s.stage}</span>
            <span className="font-mono text-stone-400">{s.rate}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white/[0.04]">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${s.rate}%`,
                background: chartFunnelBarGradient(i, stages.length),
              }}
            />
          </div>
        </div>
      ))}
      <div className="mt-3 flex items-center justify-between rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-stone-600">Avg Time to Fund</span>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-amber-300">67 days</span>
          <span className="text-[10px] text-stone-500">Target: 60 days</span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Custom tooltip for Recharts
// ---------------------------------------------------------------------------
function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-white/10 bg-[#111111] px-3 py-2 shadow-xl">
      <div className="text-xs font-semibold text-zinc-100">{label}</div>
      <div className="mt-1 text-sm font-mono text-zinc-300">{fmt.money(payload[0].value)}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Pipeline mock data (Item 12: extend exposure metrics to pipeline)
// ---------------------------------------------------------------------------
// Register I7 - illustrative only. These fifteen invented deals were merged
// with the live portfolio in the DEFAULT 'combined' view, so a live account's
// portfolio totals included deals that do not exist.
const DEMO_PIPELINE_DEALS = [
  { id: 'P-001', property: 'Halcyon Flats', type: 'Multifamily', location: 'Austin, TX', loanAmount: 32.5, ltv: 0.72, dscr: 1.28, debtYield: 0.088, stage: 'Screening', riskTier: 'low' },
  { id: 'P-002', property: 'Mesa Verde Retail', type: 'Retail', location: 'Phoenix, AZ', loanAmount: 18.0, ltv: 0.68, dscr: 1.35, debtYield: 0.095, stage: 'Screening', riskTier: 'low' },
  { id: 'P-003', property: 'Parkview Senior Housing', type: 'Senior Housing', location: 'Denver, CO', loanAmount: 24.5, ltv: 0.75, dscr: 1.20, debtYield: 0.082, stage: 'Underwriting', riskTier: 'medium' },
  { id: 'P-004', property: 'Ashford Office Park', type: 'Office', location: 'Dallas, TX', loanAmount: 41.0, ltv: 0.78, dscr: 1.14, debtYield: 0.074, stage: 'Underwriting', riskTier: 'high' },
  { id: 'P-005', property: 'Lakeshore Industrial', type: 'Industrial', location: 'Chicago, IL', loanAmount: 55.0, ltv: 0.65, dscr: 1.48, debtYield: 0.102, stage: 'Underwriting', riskTier: 'low' },
  { id: 'P-006', property: 'Briar Commons MF', type: 'Multifamily', location: 'Nashville, TN', loanAmount: 28.5, ltv: 0.73, dscr: 1.25, debtYield: 0.086, stage: 'Underwriting', riskTier: 'medium' },
  { id: 'P-007', property: 'Pinnacle Mixed-Use', type: 'Mixed-Use', location: 'Miami, FL', loanAmount: 67.0, ltv: 0.70, dscr: 1.32, debtYield: 0.091, stage: 'Committee', riskTier: 'low' },
  { id: 'P-008', property: 'Westgate Hospitality', type: 'Hospitality', location: 'Las Vegas, NV', loanAmount: 89.5, ltv: 0.76, dscr: 1.18, debtYield: 0.079, stage: 'Committee', riskTier: 'medium' },
  { id: 'P-009', property: 'Clearwater Office', type: 'Office', location: 'Tampa, FL', loanAmount: 22.0, ltv: 0.80, dscr: 1.12, debtYield: 0.071, stage: 'Screening', riskTier: 'high' },
  { id: 'P-010', property: 'Summit Self Storage', type: 'Industrial', location: 'Charlotte, NC', loanAmount: 14.5, ltv: 0.60, dscr: 1.62, debtYield: 0.114, stage: 'Screening', riskTier: 'low' },
  { id: 'P-011', property: 'Harbor View MF', type: 'Multifamily', location: 'Seattle, WA', loanAmount: 48.0, ltv: 0.74, dscr: 1.22, debtYield: 0.084, stage: 'Underwriting', riskTier: 'medium' },
  { id: 'P-012', property: 'Ridgeline Construction', type: 'Multifamily', location: 'Denver, CO', loanAmount: 35.0, ltv: 0.77, dscr: 1.16, debtYield: 0.078, stage: 'Underwriting', riskTier: 'medium' },
  { id: 'P-013', property: 'Eastpoint Retail', type: 'Retail', location: 'Atlanta, GA', loanAmount: 19.5, ltv: 0.71, dscr: 1.30, debtYield: 0.092, stage: 'Committee', riskTier: 'low' },
  { id: 'P-014', property: 'Oakwood NNN', type: 'Retail', location: 'Houston, TX', loanAmount: 26.0, ltv: 0.66, dscr: 1.44, debtYield: 0.098, stage: 'Screening', riskTier: 'low' },
  { id: 'P-015', property: 'Metro Data Center', type: 'Industrial', location: 'Dallas, TX', loanAmount: 72.0, ltv: 0.62, dscr: 1.55, debtYield: 0.108, stage: 'Underwriting', riskTier: 'low' },
]

type ExposureDeal = {
  id: string
  property: string
  type: string
  location: string
  loanAmount: number
  ltv: number
  dscr: number
  debtYield?: number
  stage: string
  riskTier?: string
}

function exposureRiskTier(ltv: number, dscr: number): 'low' | 'medium' | 'high' {
  return ltv > 0.78 || dscr < 1.15 ? 'high' : ltv > 0.70 || dscr < 1.30 ? 'medium' : 'low'
}

/** A share, or 0 when the whole is empty (never NaN on screen). */
function share(part: number, whole: number): number {
  return whole > 0 ? part / whole : 0
}

function PipelineExposure() {
  const book = usePortfolioAnalyticsBook()
  // Funded loans with LTV and DSCR on record (the whole curated set in demo).
  const portfolioDeals = useMemo(
    () => (book.isLive ? book.funded.filter((d) => d.ltv != null && d.dscr != null) : book.complete),
    [book],
  )
  const [view, setView] = useState<'portfolio' | 'combined' | 'pipeline'>('combined')
  const { focusedDealId, activateDeal } = useDealTableInteraction()

  // Live: the unfunded deals on the live book that record LTV and DSCR. The
  // fifteen illustrative pipeline deals are demo-only.
  const pipelineDeals: ExposureDeal[] = useMemo(
    () =>
      book.isLive
        ? book.pipeline.map((d) => ({
            id: d.id,
            property: d.property,
            type: d.type,
            location: d.location,
            loanAmount: d.loanAmount,
            ltv: d.ltv,
            dscr: d.dscr,
            debtYield: d.debtYield,
            stage: d.stage,
            riskTier: exposureRiskTier(d.ltv, d.dscr),
          }))
        : withDemo(DEMO_PIPELINE_DEALS, [], 'portfolio-analytics'),
    [book],
  )
  const pipelineIds = useMemo(() => new Set(pipelineDeals.map((p) => p.id)), [pipelineDeals])

  const sourceDeals = view === 'portfolio' ? portfolioDeals
    : view === 'pipeline' ? pipelineDeals
    : [...portfolioDeals, ...pipelineDeals]

  const portVol = portfolioDeals.reduce((s, d) => s + d.loanAmount, 0)
  const pipeVol = pipelineDeals.reduce((s, d) => s + d.loanAmount, 0)
  const combVol = portVol + pipeVol
  const pipeCount = pipelineDeals.length
  const portCount = portfolioDeals.length
  const pipeAvgLtv = share(pipelineDeals.reduce((s, d) => s + (d.ltv <= 1 ? d.ltv : d.ltv / 100) * d.loanAmount, 0), pipeVol)
  const pipeAvgDscr = share(pipelineDeals.reduce((s, d) => s + d.dscr * d.loanAmount, 0), pipeVol)
  const pipeHighRisk = pipelineDeals.filter(d => d.riskTier === 'high').length

  // Geographic concentration split
  const geoSplit = new Map<string, { portfolio: number; pipeline: number }>()
  portfolioDeals.forEach(d => {
    const state = stateOf(d.location)
    const cur = geoSplit.get(state) ?? { portfolio: 0, pipeline: 0 }
    geoSplit.set(state, { ...cur, portfolio: cur.portfolio + d.loanAmount })
  })
  pipelineDeals.forEach(d => {
    const state = stateOf(d.location)
    const cur = geoSplit.get(state) ?? { portfolio: 0, pipeline: 0 }
    geoSplit.set(state, { ...cur, pipeline: cur.pipeline + d.loanAmount })
  })
  const geoData = [...geoSplit.entries()]
    .map(([state, v]) => ({ state, portfolio: v.portfolio, pipeline: v.pipeline, total: v.portfolio + v.pipeline }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 8)
  const maxGeo = Math.max(...geoData.map(g => g.total))

  // Property type split
  const typeSplit = new Map<string, { portfolio: number; pipeline: number }>()
  portfolioDeals.forEach(d => {
    const cur = typeSplit.get(d.type) ?? { portfolio: 0, pipeline: 0 }
    typeSplit.set(d.type, { ...cur, portfolio: cur.portfolio + d.loanAmount })
  })
  pipelineDeals.forEach(d => {
    const cur = typeSplit.get(d.type) ?? { portfolio: 0, pipeline: 0 }
    typeSplit.set(d.type, { ...cur, pipeline: cur.pipeline + d.loanAmount })
  })
  const typeData = [...typeSplit.entries()]
    .map(([type, v]) => ({ type, portfolio: v.portfolio, pipeline: v.pipeline, total: v.portfolio + v.pipeline }))
    .sort((a, b) => b.total - a.total)
  const maxType = Math.max(...typeData.map(t => t.total))

  // Stage breakdown of pipeline
  const stageMap = new Map<string, { count: number; volume: number }>()
  pipelineDeals.forEach(d => {
    const cur = stageMap.get(d.stage) ?? { count: 0, volume: 0 }
    stageMap.set(d.stage, { count: cur.count + 1, volume: cur.volume + d.loanAmount })
  })
  const stageData = book.isLive
    ? [...stageMap.entries()].map(([stage, v]) => ({ stage, ...v }))
    : [
        { stage: 'Screening', ...( stageMap.get('Screening') ?? { count: 0, volume: 0 }) },
        { stage: 'Underwriting', ...( stageMap.get('Underwriting') ?? { count: 0, volume: 0 }) },
        { stage: 'Committee', ...( stageMap.get('Committee') ?? { count: 0, volume: 0 }) },
      ]

  // Risk distribution comparison
  const portRisk = { low: 0, medium: 0, high: 0 }
  portfolioDeals.forEach(d => {
    const ltv = d.ltv <= 1 ? d.ltv : d.ltv / 100
    const tier = ltv > 0.78 || d.dscr < 1.15 ? 'high' : ltv > 0.70 || d.dscr < 1.30 ? 'medium' : 'low'
    portRisk[tier] += d.loanAmount
  })
  const pipeRisk = { low: 0, medium: 0, high: 0 }
  pipelineDeals.forEach(d => {
    pipeRisk[d.riskTier as 'low' | 'medium' | 'high'] += d.loanAmount
  })

  const viewDeals = sourceDeals as ExposureDeal[]

  const getExposureRowValue = useCallback((row: ExposureDeal, columnIndex: number) => {
    const isPipeline = pipelineIds.has(row.id)
    const ltv = row.ltv <= 1 ? row.ltv : row.ltv / 100
    const riskTier = row.riskTier ?? (ltv > 0.78 || row.dscr < 1.15 ? 'high' : ltv > 0.70 || row.dscr < 1.30 ? 'medium' : 'low')
    switch (columnIndex) {
      case 0:
        return isPipeline ? 1 : 0
      case 1:
        return row.property
      case 2:
        return row.type
      case 3:
        return row.location
      case 4:
        return row.loanAmount
      case 5:
        return ltv
      case 6:
        return row.dscr
      case 7:
        return row.stage
      case 8:
        return riskTier === 'high' ? 2 : riskTier === 'medium' ? 1 : 0
      default:
        return ''
    }
  }, [pipelineIds])

  const { sortColumn, sortDir, toggleSort, sortedRows: sortedViewDeals } = useGenericTableSort(
    viewDeals,
    getExposureRowValue,
    4,
  )

  return (
    <div className="space-y-3">
      {/* Summary metrics */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        {[
          { label: 'Portfolio Volume', value: `$${portVol.toFixed(0)}M`, sub: `${portCount} funded deals` },
          {
            label: 'Pipeline Volume',
            value: `$${pipeVol.toFixed(0)}M`,
            sub: book.isLive && book.pipelineExcluded > 0
              ? `${pipeCount} deals · ${book.pipelineExcluded} without LTV/DSCR left out`
              : `${pipeCount} active deals`,
          },
          { label: 'Combined Exposure', value: `$${combVol.toFixed(0)}M`, sub: `${portCount + pipeCount} total` },
          { label: 'Pipeline Avg LTV', value: pipeVol > 0 ? `${(pipeAvgLtv * 100).toFixed(1)}%` : '—', sub: 'wtd by balance' },
          { label: 'Pipeline Avg DSCR', value: pipeVol > 0 ? `${pipeAvgDscr.toFixed(2)}x` : '—', sub: 'wtd by balance' },
          { label: 'High Risk (Pipeline)', value: String(pipeHighRisk), sub: 'deals flagged', alert: pipeHighRisk > 2 },
        ].map(m => (
          <div key={m.label} className="card px-3 py-2.5">
            <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-stone-600">{m.label}</div>
            <div className={`mt-1.5 text-lg font-semibold ${(m as { alert?: boolean }).alert ? 'text-red-400' : 'text-stone-100'}`}>{m.value}</div>
            <div className="text-[10px] text-stone-600">{m.sub}</div>
          </div>
        ))}
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-stone-500 font-mono uppercase tracking-wider">View:</span>
        {(['portfolio', 'combined', 'pipeline'] as const).map(v => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`px-2.5 py-1 rounded text-[10px] font-medium border transition-colors ${
              view === v
                ? 'border-amber-400/40 bg-amber-400/10 text-amber-300'
                : 'border-stone-700 text-stone-500 hover:text-stone-300'
            }`}
          >
            {v === 'portfolio' ? 'Portfolio Only' : v === 'combined' ? 'Combined' : 'Pipeline Only'}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3 text-[10px] text-stone-600">
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: CHART_PORTFOLIO_COLOR }} /> Funded Portfolio</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: CHART_PIPELINE_COLOR }} /> Pipeline</span>
        </div>
      </div>

      {/* Row 1: Geographic + Property Type split */}
      <div className="grid gap-3 xl:grid-cols-2">
        {/* Geographic split */}
        <div className="card px-5 py-4">
          <div className="terminal-kicker mb-4">Geographic Exposure — Portfolio vs Pipeline</div>
          <div className="space-y-3">
            {geoData.map(g => {
              const portPct = (g.portfolio / maxGeo) * 100
              const pipePct = (g.pipeline / maxGeo) * 100
              return (
                <div key={g.state} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-300 w-8">{g.state}</span>
                    <div className="flex items-center gap-2 text-[10px] font-mono text-stone-500">
                      {g.portfolio > 0 && <span style={{ color: CHART_PORTFOLIO_COLOR }}>${g.portfolio.toFixed(0)}M</span>}
                      {g.pipeline > 0 && <span style={{ color: CHART_PIPELINE_COLOR }}>${g.pipeline.toFixed(0)}M</span>}
                    </div>
                  </div>
                  <div className="chart-viz-track h-3 flex overflow-hidden rounded-full">
                    <div className="chart-viz-bar h-full rounded-l-full transition-all duration-500" style={{ width: `${portPct}%`, background: CHART_PORTFOLIO_COLOR }} />
                    <div className="chart-viz-bar h-full rounded-r-full transition-all duration-500" style={{ width: `${pipePct}%`, background: CHART_PIPELINE_COLOR }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Property type split */}
        <div className="card px-5 py-4">
          <div className="terminal-kicker mb-4">Property Type Exposure — Portfolio vs Pipeline</div>
          <div className="space-y-3">
            {typeData.map(t => {
              const portPct = (t.portfolio / maxType) * 100
              const pipePct = (t.pipeline / maxType) * 100
              return (
                <div key={t.type} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-300 w-28 truncate">{t.type}</span>
                    <div className="flex items-center gap-2 text-[10px] font-mono text-stone-500">
                      {t.portfolio > 0 && <span style={{ color: CHART_PORTFOLIO_COLOR }}>${t.portfolio.toFixed(0)}M</span>}
                      {t.pipeline > 0 && <span style={{ color: CHART_PIPELINE_COLOR }}>${t.pipeline.toFixed(0)}M</span>}
                    </div>
                  </div>
                  <div className="chart-viz-track h-3 flex overflow-hidden rounded-full">
                    <div className="chart-viz-bar h-full rounded-l-full transition-all duration-500" style={{ width: `${portPct}%`, background: CHART_PORTFOLIO_COLOR }} />
                    <div className="chart-viz-bar h-full rounded-r-full transition-all duration-500" style={{ width: `${pipePct}%`, background: CHART_PIPELINE_COLOR }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Row 2: Pipeline stage breakdown + risk comparison */}
      <div className="grid gap-3 xl:grid-cols-2">
        {/* Pipeline stage breakdown */}
        <div className="card px-5 py-4">
          <div className="terminal-kicker mb-4">Pipeline Stage Breakdown</div>
          <div className="space-y-3">
            {stageData.map(s => (
              <div key={s.stage} className="rounded-lg border border-amber-400/10 bg-black/20 px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-stone-200">{s.stage}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-stone-500">{s.count} deals</span>
                    <span className="text-xs font-mono" style={{ color: CHART_PIPELINE_COLOR }}>${s.volume.toFixed(1)}M</span>
                  </div>
                </div>
                <div className="chart-viz-track chart-viz-bar--rounded h-1.5">
                  <div
                    className="chart-viz-bar chart-viz-bar--rounded h-full transition-all"
                    style={{ width: `${share(s.volume, pipeVol) * 100}%`, background: CHART_PIPELINE_COLOR }}
                  />
                </div>
                <div className="mt-1 text-[10px] text-stone-600">{(share(s.volume, pipeVol) * 100).toFixed(0)}% of pipeline volume</div>
              </div>
            ))}
          </div>
        </div>

        {/* Risk tier comparison */}
        <div className="card px-5 py-4">
          <div className="terminal-kicker mb-4">Risk Tier Distribution — Portfolio vs Pipeline</div>
          <div className="space-y-4">
            {(['low', 'medium', 'high'] as const).map(tier => {
              const portPct = share(portRisk[tier], portVol)
              const pipePct = share(pipeRisk[tier], pipeVol)
              const tierColor = chartRiskTierColor(tier)
              return (
                <div key={tier}>
                  <div className="flex items-center justify-between mb-2 text-xs">
                    <span className="capitalize font-medium" style={{ color: tierColor }}>{tier} Risk</span>
                    <div className="flex items-center gap-3 text-[10px] font-mono">
                      <span style={{ color: CHART_PORTFOLIO_COLOR }}>Port: {(portPct * 100).toFixed(0)}%</span>
                      <span style={{ color: CHART_PIPELINE_COLOR }}>Pipe: {(pipePct * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="chart-viz-track chart-viz-bar--rounded h-2">
                      <div
                        className="chart-viz-bar chart-viz-bar--rounded h-full transition-all"
                        style={{ width: `${portPct * 100}%`, background: CHART_PORTFOLIO_COLOR }}
                      />
                    </div>
                    <div className="chart-viz-track chart-viz-bar--rounded h-2">
                      <div
                        className="chart-viz-bar chart-viz-bar--rounded h-full transition-all"
                        style={{ width: `${pipePct * 100}%`, background: CHART_PIPELINE_COLOR }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="mt-4 p-3 rounded-lg border border-amber-400/10 bg-amber-400/[0.03]">
            <div className="text-[10px] font-mono uppercase tracking-wider text-stone-600 mb-1">Concentration Alert</div>
            <div className="text-xs text-stone-300">
              Pipeline adds <span className="text-blue-300 font-medium">${pipeVol.toFixed(0)}M</span> of exposure across{' '}
              <span className="text-blue-300 font-medium">{pipelineDeals.length} deals</span>.{' '}
              {(share(pipeRisk.high, pipeVol) * 100) > 15
                ? <span className="text-red-400">High-risk pipeline concentration ({(share(pipeRisk.high, pipeVol) * 100).toFixed(0)}%) warrants review.</span>
                : <span className="text-emerald-400">Risk profile consistent with funded book.</span>
              }
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Combined deal-level table */}
      <div className="card overflow-hidden">
        <div className="border-b border-amber-400/10 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="terminal-kicker">
              {view === 'portfolio' ? 'Portfolio Deals' : view === 'pipeline' ? 'Pipeline Deals' : 'Combined Deal Register'}
            </div>
            <p className="mt-0.5 text-xs text-stone-500">{viewDeals.length} deals{view !== 'portfolio' ? ' · pipeline highlighted in blue' : ''}</p>
          </div>
        </div>
        <div className="overflow-x-auto max-h-80">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className={dealTableHeadRowClass()}>
                <GenericSortHeader label="Source" columnIndex={0} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="px-4 py-2.5 text-[10px] font-medium bg-[#0b0b0b]" />
                <GenericSortHeader label="Property" columnIndex={1} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="px-4 py-2.5 text-[10px] font-medium bg-[#0b0b0b]" />
                <GenericSortHeader label="Type" columnIndex={2} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="px-4 py-2.5 text-[10px] font-medium bg-[#0b0b0b]" />
                <GenericSortHeader label="Location" columnIndex={3} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="px-4 py-2.5 text-[10px] font-medium bg-[#0b0b0b]" />
                <GenericSortHeader label="Amount" columnIndex={4} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="px-4 py-2.5 text-[10px] font-medium bg-[#0b0b0b]" />
                <GenericSortHeader label="LTV" columnIndex={5} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="px-4 py-2.5 text-[10px] font-medium bg-[#0b0b0b]" />
                <GenericSortHeader label="DSCR" columnIndex={6} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="px-4 py-2.5 text-[10px] font-medium bg-[#0b0b0b]" />
                <GenericSortHeader label="Stage" columnIndex={7} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="px-4 py-2.5 text-[10px] font-medium bg-[#0b0b0b]" />
                <GenericSortHeader label="Risk" columnIndex={8} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="px-4 py-2.5 text-[10px] font-medium bg-[#0b0b0b]" />
              </tr>
            </thead>
            <tbody>
              {sortedViewDeals.map(deal => {
                const ltv = (deal.ltv <= 1 ? deal.ltv : deal.ltv / 100)
                const riskTier = deal.riskTier ?? (ltv > 0.78 || deal.dscr < 1.15 ? 'high' : ltv > 0.70 || deal.dscr < 1.30 ? 'medium' : 'low')
                const isPipeline = pipelineIds.has(deal.id)
                const riskColor = riskTier === 'high' ? 'border-red-500/30 bg-red-500/10 text-red-300' : riskTier === 'medium' ? 'border-amber-400/30 bg-amber-400/10 text-amber-200' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                const ltvColor = ltv > 0.78 ? 'text-red-400' : ltv > 0.70 ? 'text-amber-300' : 'text-emerald-400'
                const dscrColor = deal.dscr < 1.15 ? 'text-red-400' : deal.dscr < 1.30 ? 'text-amber-300' : 'text-emerald-400'
                return (
                  <DealTableRow
                    key={deal.id}
                    dealId={deal.id}
                    isFocused={focusedDealId === deal.id}
                    onActivate={isLoanTableId(deal.id) ? activateDeal : (id) => activateDeal(id, { openProfile: false })}
                    openProfileOnClick={false}
                    className={isPipeline ? 'bg-blue-400/[0.02]' : ''}
                  >
                    <td className="px-4 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium border ${isPipeline ? 'border-blue-400/30 bg-blue-400/10 text-blue-300' : 'border-amber-400/20 bg-amber-400/[0.06] text-amber-400/80'}`}>
                        {isPipeline ? 'Pipeline' : 'Portfolio'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-stone-200">{deal.property}</td>
                    <td className="px-4 py-2 text-xs text-stone-400">{deal.type}</td>
                    <td className="px-4 py-2 text-xs text-stone-400">{deal.location}</td>
                    <td className="px-4 py-2 text-xs font-mono text-stone-200">${deal.loanAmount.toFixed(1)}M</td>
                    <td className={`px-4 py-2 text-xs font-mono ${ltvColor}`}>{(ltv * 100).toFixed(0)}%</td>
                    <td className={`px-4 py-2 text-xs font-mono ${dscrColor}`}>{deal.dscr.toFixed(2)}x</td>
                    <td className="px-4 py-2 text-xs text-stone-400">{deal.stage}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${riskColor}`}>
                        {riskTier}
                      </span>
                    </td>
                  </DealTableRow>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function PortfolioRiskHeatmap({ deals }: { deals: Loan[] }) {
  const { focusedDealId, activateDeal } = useDealTableInteraction()
  const { sortKey, sortDir, toggleSort, sortedLoans } = useLoanTableSort(deals, 'loanAmount', 'desc')

  return (
    <div className="card overflow-hidden">
      <div className="border-b border-amber-400/10 px-5 py-3">
        <div className="terminal-kicker">Portfolio Risk Heatmap</div>
        <p className="mt-1 text-xs text-stone-500">Deal-level credit metrics with risk-tier coloring</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={dealTableHeadRowClass()}>
              <DealTableSortHeader label="Deal" col="property" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="px-4 py-3 text-[10px] font-medium" />
              <DealTableSortHeader label="Type" col="type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="px-4 py-3 text-[10px] font-medium" />
              <DealTableSortHeader label="Location" col="location" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="px-4 py-3 text-[10px] font-medium" />
              <DealTableSortHeader label="Amount" col="loanAmount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" className="px-4 py-3 text-[10px] font-medium" />
              <DealTableSortHeader label="LTV" col="ltv" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" className="px-4 py-3 text-[10px] font-medium" />
              <DealTableSortHeader label="DSCR" col="dscr" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" className="px-4 py-3 text-[10px] font-medium" />
              <DealTableSortHeader label="Debt Yield" col="debtYield" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" className="px-4 py-3 text-[10px] font-medium" />
              <DealTableSortHeader label="Stage" col="stage" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="px-4 py-3 text-[10px] font-medium" />
              <DealTableSortHeader label="Risk" col="risk" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="px-4 py-3 text-[10px] font-medium" />
            </tr>
          </thead>
          <tbody>
            {sortedLoans.map((deal) => {
              // A funded live loan can lack LTV or DSCR; it is shown as not
              // recorded, never graded as if the missing figure were zero.
              const known = deal.ltv != null && deal.dscr != null
              const ltv = deal.ltv == null ? null : deal.ltv <= 1 ? deal.ltv : deal.ltv / 100
              const riskTier = !known ? 'unknown' : riskTierRank(deal) === 2 ? 'high' : riskTierRank(deal) === 1 ? 'medium' : 'low'
              const riskColor = riskTier === 'unknown' ? 'border-stone-600/30 bg-white/[0.02] text-stone-400' : riskTier === 'high' ? 'border-red-500/30 bg-red-500/10 text-red-300' : riskTier === 'medium' ? 'border-amber-400/30 bg-amber-400/10 text-amber-200' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
              const ltvColor = ltv == null ? 'text-stone-500' : ltv > 0.78 ? 'text-red-400' : ltv > 0.70 ? 'text-amber-300' : 'text-emerald-400'
              const dscrColor = deal.dscr == null ? 'text-stone-500' : deal.dscr < 1.15 ? 'text-red-400' : deal.dscr < 1.30 ? 'text-amber-300' : 'text-emerald-400'
              const dyColor = (deal.debtYield ?? 0) < 0.075 ? 'text-red-400' : (deal.debtYield ?? 0) < 0.09 ? 'text-amber-300' : 'text-emerald-400'

              return (
                <DealTableRow
                  key={deal.id}
                  dealId={deal.id}
                  isFocused={focusedDealId === deal.id}
                  onActivate={activateDeal}
                  openProfileOnClick={false}
                >
                  <td className="px-4 py-2.5 text-xs text-amber-300">{deal.property}</td>
                  <td className="px-4 py-2.5 text-xs text-stone-400">{deal.type}</td>
                  <td className="px-4 py-2.5 text-xs text-stone-400">{deal.location}</td>
                  <td className="px-4 py-2.5 text-xs font-mono text-stone-200">{fmt.money(deal.loanAmount)}</td>
                  <td className={`px-4 py-2.5 text-xs font-mono ${ltvColor}`}>{ltv == null ? '—' : `${(ltv * 100).toFixed(0)}%`}</td>
                  <td className={`px-4 py-2.5 text-xs font-mono ${dscrColor}`}>{deal.dscr == null ? '—' : `${deal.dscr.toFixed(2)}x`}</td>
                  <td className={`px-4 py-2.5 text-xs font-mono ${dyColor}`}>{deal.debtYield ? (deal.debtYield * 100).toFixed(1) + '%' : '-'}</td>
                  <td className="px-4 py-2.5 text-xs text-stone-400">{deal.stage}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${riskColor}`}>
                      {riskTier}
                    </span>
                  </td>
                </DealTableRow>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

type AnalyticsTab = 'overview' | 'stress-test' | 'ltv' | 'market' | 'risk' | 'var' | 'pipeline'

const ANALYTICS_TABS: { id: AnalyticsTab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <LayoutDashboard size={12} /> },
  { id: 'pipeline', label: 'Pipeline Exposure', icon: <Layers size={12} /> },
  { id: 'stress-test', label: 'Stress Testing', icon: <TrendingDown size={12} /> },
  { id: 'ltv', label: 'LTV Analysis', icon: <Percent size={12} /> },
  { id: 'var', label: 'Value at Risk', icon: <TrendingDown size={12} /> },
  { id: 'market', label: 'Market Exposure', icon: <Globe size={12} /> },
  { id: 'risk', label: 'Risk Monitor', icon: <Shield size={12} /> },
]

/**
 * Shown on the live book in place of an analysis that has nothing real to run
 * on. Says what the analysis needs and which funded loans lack it.
 */
function AnalysisUnavailable({ book }: { book: AnalyticsBook }) {
  return (
    <div className="card px-5 py-6">
      <NoSource>
        {book.funded.length === 0 ? (
          <>No funded loans on the live book yet. This analysis runs on funded loans that record property value, NOI, LTV and DSCR.</>
        ) : (
          <>
            This analysis needs property value, NOI, LTV and DSCR on each funded loan. None of the{' '}
            {book.funded.length} funded loan{book.funded.length === 1 ? '' : 's'} records all four
            {book.incomplete.length > 0 && (
              <> ({book.incomplete.map((d) => `${d.property}: no ${d.missing.join(', ')}`).join('; ')})</>
            )}
            .
          </>
        )}
      </NoSource>
    </div>
  )
}

function ExcludedNote({ book }: { book: AnalyticsBook & { isLive: boolean } }) {
  if (!book.isLive || book.incomplete.length === 0) return null
  return (
    <p className="text-[10px] text-stone-500">
      Left out for missing inputs:{' '}
      {book.incomplete.map((d) => `${d.property} (no ${d.missing.join(', ')})`).join('; ')}.
    </p>
  )
}

export default function PortfolioAnalytics() {
  const book = usePortfolioAnalyticsBook()
  const deals = book.funded
  const data = usePortfolioData(deals, book.isLive)
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview')
  const noRiskInputs = book.isLive && book.complete.length === 0

  return (
    <div className="space-y-3 p-4">
      {/* Tab Bar */}
      <div className="flex items-center gap-1 border-b border-amber-400/10">
        {ANALYTICS_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium border-b-2 transition-all ${
              activeTab === tab.id
                ? 'border-amber-400 text-amber-300'
                : 'border-transparent text-stone-500 hover:text-stone-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* AnalysisUnavailable already names every left-out loan; say it once. */}
      {activeTab !== 'overview' && activeTab !== 'pipeline' && !noRiskInputs && <ExcludedNote book={book} />}
      {activeTab === 'pipeline' &&
        (book.isLive && book.pipeline.length === 0 && !book.funded.some((d) => d.ltv != null && d.dscr != null) ? (
          <div className="card px-5 py-6">
            <NoSource>No deal on the live book, funded or in the pipeline, records both LTV and DSCR yet.</NoSource>
          </div>
        ) : (
          <PipelineExposure />
        ))}
      {activeTab === 'stress-test' && (noRiskInputs ? <AnalysisUnavailable book={book} /> : <CapRateStressTest />)}
      {activeTab === 'ltv' && (noRiskInputs ? <AnalysisUnavailable book={book} /> : <PortfolioLtvAnalysis />)}
      {activeTab === 'market' && (noRiskInputs ? <AnalysisUnavailable book={book} /> : <MarketExposureAnalysis />)}
      {activeTab === 'var' && (noRiskInputs ? <AnalysisUnavailable book={book} /> : <ValueAtRiskModel />)}
      {activeTab === 'risk' && (noRiskInputs ? <AnalysisUnavailable book={book} /> : <RiskMonitoringDashboard />)}

      {activeTab === 'overview' && (<>
      {/* Metric Strip */}
      <MetricStrip metrics={data.metrics} />

      {/* Row 2: Concentration + Performance Attribution */}
      <div className="grid gap-3 xl:grid-cols-2">
        {/* Concentration Risk */}
        <div className="card px-5 py-4">
          <div className="terminal-kicker mb-4">Concentration Risk</div>
          <div className="grid gap-6 md:grid-cols-2">
            <ConcentrationBar data={data.geographic} label="Geographic" />
            <ConcentrationBar data={data.propertyTypes} label="Property Type" />
          </div>

          {/* Pie Chart */}
          <div className="mt-5 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.propertyTypes}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                  nameKey="name"
                >
                  {data.propertyTypes.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={chartPieColor(index)} />
                  ))}
                </Pie>
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(value: number) => fmt.money(value)} />
                <Legend iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Performance Attribution */}
        <div className="card px-5 py-4">
          <div className="terminal-kicker mb-4">Performance Attribution</div>

          {/* Revenue by Product */}
          <div className="mb-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-600 mb-3">Revenue by Product</div>
            {book.isLive && data.revenueByProduct.length === 0 && (
              <NoSource>No fee or interest income is recorded on the funded loans, so revenue by product cannot be shown.</NoSource>
            )}
            <div className="space-y-2">
              {data.revenueByProduct.map((item, i) => (
                <div key={item.product} className="flex items-center gap-3">
                  <span className="w-28 text-xs text-stone-300 truncate">{item.product}</span>
                  <div className="flex-1 h-2 overflow-hidden rounded-full bg-white/[0.04]">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${(item.revenue / (data.revenueByProduct[0]?.revenue || 1)) * 100}%`,
                        background: CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length],
                      }}
                    />
                  </div>
                  <span className="w-16 text-right font-mono text-xs text-stone-400">${item.revenue.toFixed(1)}M</span>
                </div>
              ))}
            </div>
          </div>

          {/* Originator Performance */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-600 mb-3">Originator Performance</div>
            {data.originators.length === 0 && <NoSource>No funded loans on the live book yet.</NoSource>}
            <div className="space-y-1.5">
              {data.originators.map((orig, i) => (
                <div key={orig.name} className="flex items-center justify-between rounded-lg border border-amber-400/6 bg-white/[0.01] px-3 py-2 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md border border-amber-400/15 bg-amber-400/[0.06] text-[10px] font-bold text-amber-300">
                      {i + 1}
                    </span>
                    <span className="text-xs font-medium text-stone-200">{orig.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xs text-stone-400">{fmt.money(orig.volume)}</span>
                    <span className="text-[10px] text-stone-500">{orig.deals} deals</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Maturity Schedule + Conversion Funnel */}
      <div className="grid gap-3 xl:grid-cols-2">
        {/* Maturity Schedule */}
        <div className="card px-5 py-4">
          <div className="terminal-kicker mb-4">Maturity Schedule</div>
          {data.maturitySchedule.length === 0 ? (
            <NoSource>
              {deals.length === 0
                ? 'No funded loans on the live book yet.'
                : 'No maturity date is recorded on the funded loans.'}
            </NoSource>
          ) : (<>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.maturitySchedule} barCategoryGap="20%">
                <CartesianGrid {...chartGridProps()} />
                <XAxis dataKey="quarter" {...chartAxisProps()} tickLine={false} />
                <YAxis {...chartAxisProps()} tickLine={false} tickFormatter={(v) => `$${v}M`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                  {data.maturitySchedule.map((_entry, index) => (
                    <Cell
                      key={`bar-${index}`}
                      fill={chartSeriesColor(index)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-[10px] text-stone-500">
            {data.maturitySchedule.slice(0, 4).map((entry, index) => (
              <div key={entry.quarter} className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: chartSeriesColor(index) }} />
                {entry.quarter}
              </div>
            ))}
          </div>
          </>)}
        </div>

        {/* Conversion Funnel */}
        <div className="card px-5 py-4">
          <div className="terminal-kicker mb-4">Conversion Funnel</div>
          {book.isLive ? (
            <NoSource>
              Stage-to-stage conversion and time in each stage need the history of stage moves, which the
              live book does not keep yet. Current stage counts are on the Pipeline.
            </NoSource>
          ) : (<>
          <FunnelChart stages={data.funnelStages} />

          {/* Stage velocity */}
          <div className="mt-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-600 mb-2">Stage Velocity (avg days)</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { stage: 'Intake', days: 4.2, target: 3 },
                { stage: 'Screen', days: 6.8, target: 7 },
                { stage: 'U/W', days: 12.5, target: 10 },
                { stage: 'Committee', days: 8.1, target: 8 },
                { stage: 'Close', days: 18.3, target: 15 },
                { stage: 'Fund', days: 5.2, target: 6 },
              ].map((v) => (
                <div key={v.stage} className="rounded-lg border border-amber-400/8 bg-white/[0.015] px-2.5 py-2">
                  <div className="text-[10px] text-stone-500">{v.stage}</div>
                  <div className={`mt-1 text-sm font-semibold ${v.days <= v.target ? 'text-emerald-400' : v.days <= v.target * 1.2 ? 'text-amber-300' : 'text-red-400'}`}>
                    {v.days.toFixed(1)}d
                  </div>
                  <div className="text-[9px] text-stone-600">target: {v.target}d</div>
                </div>
              ))}
            </div>
          </div>
          </>)}
        </div>
      </div>

      <PortfolioRiskHeatmap deals={deals} />
      </>)}
    </div>
  )
}
